/**
 * mega-nav-shop.js
 * Self-contained custom element. Owns its own trigger and dropdown, so there is
 * no interaction with Horizon's hover system — open/close, rail switching, and
 * the carousels are all controlled here. Click-only by design.
 *
 * Intentionally does NOT extend Horizon's Component base class so it survives
 * theme updates.
 */
class MegaNavShop extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-mega-toggle]');
    this.dropdown = this.querySelector('[data-mega-dropdown]');
    this.triggers = [...this.querySelectorAll('[data-mega-trigger]')];
    this.panels = [...this.querySelectorAll('[data-mega-panel]')];
    this.isOpen = false;

    // Top-level open/close.
    this.toggleBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    // Rail: click to switch panel (no hover).
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.activate(index));
    });

    // Carousels.
    this.querySelectorAll('[data-mega-carousel]').forEach((c) => this.initCarousel(c));

    // Close on outside click / Escape.
    this._onDocClick = (e) => {
      if (this.isOpen && !this.contains(e.target)) this.close();
    };
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
        this.toggleBtn?.focus();
      }
    };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    // Carousel measurements were taken while the panel was display:none;
    // refresh the active one now that it's visible.
    const active = this.panels.find((p) => !p.hasAttribute('hidden'));
    active?.querySelector('[data-mega-carousel]')?.update?.();
  }

  close() {
    this.isOpen = false;
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
  }

  activate(index) {
    this.triggers.forEach((trigger, i) => {
      const active = i === index;
      trigger.classList.toggle('is-active', active);
      trigger.setAttribute('aria-expanded', String(active));
    });
    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('hidden', !active);
    });
    this.panels[index]?.querySelector('[data-mega-carousel]')?.update?.();
  }

  initCarousel(carousel) {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;

    const step = () => {
      const card = track.querySelector('.mega-shop__card');
      if (!card) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return (card.offsetWidth + gap) * 3;
    };

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 1;
      prev.disabled = track.scrollLeft <= 0;
      next.disabled = track.scrollLeft >= maxScroll;
    };
    carousel.update = update;

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(track);
    update();
  }
}

if (!customElements.get('mega-nav-shop')) {
  customElements.define('mega-nav-shop', MegaNavShop);
}
